import React, { createContext, useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import {
  getGroupConfigurations,
  GroupConfigurations,
  linkageLabels,
  LinkageField,
  SetupGroup,
  SetupSystem,
  SystemRevision,
} from "../../api/syncConfiguration";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { toReadableText } from "../utils/utils";

export const SourceSystemsContext = createContext<SystemRevision[]>([]);
export function NestedSystemHeadings({
  mainSystemId,
}: {
  mainSystemId: number;
}) {
  const systems = useContext(SourceSystemsContext);
  return (
    <>
      {systems
        .filter((s) => s.system_id !== mainSystemId)
        .map((s) => (
          <Typography
            key={s.system_id}
            component="h3"
            variant="subtitle1"
            id={`system-${s.system_id}`}
            tabIndex={-1}
            sx={{ scrollMarginTop: "100px" }}
          >
            {s.name} ({toReadableText(s.system_type)})
          </Typography>
        ))}
    </>
  );
}

export function useSourceGroups(enabled: boolean) {
  const [data, setData] = useState<GroupConfigurations>({
    groups: [],
    system_revisions: [],
  });
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(false);
  const refresh = async () => {
    setLoading(true);
    setError(false);
    try {
      setData(await getGroupConfigurations());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (enabled) {
      void refresh();
    }
  }, [enabled]);
  return { data, loading, error, refresh };
}

const highlightTimers = new WeakMap<
  HTMLElement,
  ReturnType<typeof setTimeout>
>();
export function navigateToSystem(id: number) {
  const target = document.getElementById(`system-${id}`);
  if (!target) return false;
  let ancestor = target.parentElement;
  while (ancestor) {
    if (ancestor instanceof HTMLDetailsElement) ancestor.open = true;
    ancestor = ancestor.parentElement;
  }
  const card = target.closest<HTMLElement>("[data-source-card]") || target;
  window.history.replaceState(null, "", `#system-${id}`);
  target.focus({ preventScroll: true });
  card.scrollIntoView({
    behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
      ? "auto"
      : "smooth",
    block: "start",
  });
  clearTimeout(highlightTimers.get(card));
  card.setAttribute("data-jump-highlight", "true");
  highlightTimers.set(
    card,
    setTimeout(() => card.removeAttribute("data-jump-highlight"), 1800)
  );
  return true;
}

function SystemLink({
  system,
  visibleIds,
}: {
  system: SetupSystem;
  visibleIds: Set<number>;
}) {
  return visibleIds.has(system.id) ? (
    <Link
      color="info.dark"
      underline="always"
      href={`#system-${system.id}`}
      onClick={(event) => {
        if (navigateToSystem(system.id)) event.preventDefault();
      }}
    >
      {system.name}
    </Link>
  ) : (
    <>{system.name}</>
  );
}

function AttributeRows({
  group,
  fields,
}: {
  group: SetupGroup;
  fields: LinkageField[];
}) {
  return (
    <Box
      component="dl"
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(110px, 0.9fr) minmax(0, 1.1fr)",
        columnGap: 2,
        rowGap: 1.25,
        my: 1.5,
      }}
    >
      {fields.map((field) => {
        const ref = group.attributes[field];
        const value =
          ref.id != null
            ? `${ref.label || "Unavailable reference"} (#${ref.id})${
                !ref.available && ref.label ? " — unavailable" : ""
              }`
            : ref.applicable
            ? "Not configured"
            : "Not applicable";
        const side = field.startsWith("system_a_") ? "A" : "B";
        const system = side === "A" ? group.system_a : group.system_b;
        return (
          <React.Fragment key={field}>
            <Typography
              component="dt"
              variant="body2"
              color="text.secondary"
              title={system.name}
            >
              {side} · {field.includes("account") ? "Account" : "Contact"}{" "}
              linkage
            </Typography>
            <Typography
              component="dd"
              variant="body2"
              sx={{
                m: 0,
                fontWeight: ref.applicable || ref.id != null ? 600 : 400,
                color:
                  ref.applicable && ref.id == null
                    ? "warning.dark"
                    : "text.primary",
              }}
            >
              {value}
            </Typography>
          </React.Fragment>
        );
      })}
    </Box>
  );
}

export function GroupSummary({
  group,
  visibleIds,
  systemIds = [],
}: {
  group: SetupGroup;
  visibleIds: Set<number>;
  systemIds?: number[];
}) {
  const [expanded, setExpanded] = useState(false);
  const endpoints = [group.system_a, group.system_b];
  const other = endpoints.filter((system) => !systemIds.includes(system.id));
  const titleSystems = other.length === 1 ? other : endpoints;
  const fields = Object.keys(linkageLabels) as LinkageField[];
  const prominent = fields.filter(
    (field) =>
      group.attributes[field].applicable || group.attributes[field].id != null
  );
  const inapplicable = fields.filter((field) => !prominent.includes(field));
  return (
    <Box
      component="article"
      aria-label={`Sync group ${group.id}`}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        p: 2,
        overflowWrap: "anywhere",
      }}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="h4"
            variant="subtitle1"
            sx={{ fontWeight: 600 }}
          >
            {titleSystems.map((system, index) => (
              <React.Fragment key={system.id}>
                {index > 0 && " · "}
                <SystemLink system={system} visibleIds={visibleIds} />
              </React.Fragment>
            ))}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {titleSystems
              .map((system) => toReadableText(system.system_type))
              .join(" · ")}{" "}
            · Group #{group.id}
          </Typography>
        </Box>
        <IconButton
          aria-label={`${expanded ? "Collapse" : "Expand"} sync group ${
            group.id
          }`}
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
          size="small"
        >
          <ExpandMoreIcon
            sx={{ transform: expanded ? "rotate(180deg)" : undefined }}
          />
        </IconButton>
      </Stack>
      <Collapse in={expanded}>
        {endpoints
          .filter((system) => !visibleIds.has(system.id))
          .map((system) => (
            <Alert key={system.id} severity="info" sx={{ mt: 1.5, py: 0.5 }}>
              <strong>{system.name}: configuration unavailable here.</strong>{" "}
              Its configuration cannot be opened on this page. Saved sync
              settings are shown below.
            </Alert>
          ))}
        <AttributeRows group={group} fields={prominent} />
        {group.reference_limits && (
          <Stack direction="row" flexWrap="wrap" gap={3} sx={{ py: 1 }}>
            <Typography variant="body2">
              Search limit <strong>{group.max_reference_data_searches}</strong>
            </Typography>
            <Typography variant="body2">
              Refresh limit{" "}
              <strong>{group.max_reference_data_refreshes}</strong>
            </Typography>
          </Stack>
        )}
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {endpoints.map((system, index) => (
            <Typography key={system.id} variant="body2">
              <strong>System {index === 0 ? "A" : "B"}: </strong>
              <SystemLink system={system} visibleIds={visibleIds} /> ·{" "}
              {toReadableText(system.system_type)}
            </Typography>
          ))}
        </Stack>
        <AttributeRows group={group} fields={inapplicable} />
        {!group.reference_limits && (
          <Typography variant="caption" color="text.secondary">
            Reference-data limits: not applicable. Saved search limit:{" "}
            {group.max_reference_data_searches}; refresh limit:{" "}
            {group.max_reference_data_refreshes}.
          </Typography>
        )}
      </Collapse>
    </Box>
  );
}

export function SourceGroupPanel({
  groups,
  visibleIds,
  loading,
  error,
  retry,
  systemIds = [],
}: {
  groups: SetupGroup[];
  visibleIds: Set<number>;
  loading: boolean;
  error: boolean;
  retry: () => void;
  systemIds?: number[];
}) {
  return (
    <Box component="aside" aria-label="Sync groups" sx={{ minWidth: 0 }}>
      <Typography
        variant="h6"
        component="h3"
        sx={{ mb: 2, fontFamily: "inherit", fontWeight: 600 }}
      >
        Sync groups
      </Typography>
      {loading && (
        <Stack direction="row" spacing={1}>
          <CircularProgress size={20} />
          <Typography>Loading sync groups…</Typography>
        </Stack>
      )}
      {error && (
        <Alert severity="error" action={<Button onClick={retry}>Retry</Button>}>
          Unable to load sync groups.
        </Alert>
      )}
      {!loading && !error && groups.length === 0 && (
        <Typography color="text.secondary" variant="body2">
          No sync groups configured
        </Typography>
      )}
      <Stack spacing={2}>
        {groups.map((group) => (
          <GroupSummary
            key={group.id}
            group={group}
            visibleIds={visibleIds}
            systemIds={systemIds}
          />
        ))}
      </Stack>
    </Box>
  );
}
